import type { Nullable } from "@core/domain/types/nullable.type";
import { BaseViewModel } from "@iot-platform/core";
import type {
    Route,
    RouteFilter,
    RoutePoint,
} from "@station/domain/models/route.model";
import { RouteGetAllUseCase } from "@station/domain/usecases/route-get-all.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class RouteViewmodel extends BaseViewModel {
    @observable
    route: Nullable<Route> = null;

    @observable
    stationId: Nullable<string> = null;

    @observable
    routeCoordinates: RoutePoint[] = [];

    @observable
    from: DateTime = DateTime.now().minus({ days: 1 });

    @observable
    to: DateTime = DateTime.now();

    @observable
    errors: { from?: string; to?: string } = {};

    @observable
    initialLoading = true;

    constructor(
        @inject(RouteGetAllUseCase)
        private readonly routeGetAllUseCase: RouteGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.getAllRoutes();
    }

    @action
    async getAllRoutes(): Promise<void> {
        if (!this.stationId) throw new Error("Missing stationId");

        const routeFilter: RouteFilter = {
            from: this.from.toSeconds(),
            to: this.to.toSeconds(),
            stationId: Number(this.stationId),
        };

        const routeResult = await this.routeGetAllUseCase.execute(routeFilter);

        runInAction(() => {
            this.route = routeResult;
            this.updateRouteCoordinates();
            this.initialLoading = false;
        });
    }

    @action updateRouteCoordinates(): void {
        if (!this.route?.tracks) return;
        this.routeCoordinates = this.route.tracks.map((track) => ({
            lat: track.latitude,
            lng: track.longitude,
            status: track.status,
            timestamp: track.timestamp,
        }));
    }

    @action
    setStationId(stationId: string): void {
        if (!stationId) {
            throw new Error("Missing stationId");
        }

        this.stationId = stationId;
    }
}
